import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { hierarchy, tree as d3Tree } from 'd3-hierarchy';
import api from '../services/api';
import { matchesSearch } from '../utils/viewStatusHelpers';
import { formatDateShort } from '../utils/dateUtils';
import './ReflectionTree.css';

const CARD_W = 120;
const CARD_H = 62;
const H_GAP = 12;
const V_GAP = 30;

/**
 * Get the media type icon for a reflection
 */
function getMediaIcon(reflection) {
  if (reflection.video_key) return '\uD83C\uDFA5';
  if (reflection.audio_key) return '\uD83C\uDFA4';
  if (reflection.text_content?.trim()) return '\uD83D\uDCDD';
  return '\uD83D\uDCC4';
}

/**
 * Format a compact date string — delegates to dateUtils with timezone
 */
function formatCompactDate(dateStr, timezone) {
  return formatDateShort(dateStr, timezone);
}

/**
 * Build a tree structure from a flat array of reflections.
 * Adds placeholder child nodes for unanswered follow-up suggestions.
 * Returns the root node with nested children.
 */
function buildTree(flatReflections, enableFollowups = true) {
  const byId = {};
  flatReflections.forEach(r => { byId[r.id] = { ...r, children: [] }; });

  let root = null;
  flatReflections.forEach(r => {
    const node = byId[r.id];
    if (r.parent_reflection_id && byId[r.parent_reflection_id]) {
      byId[r.parent_reflection_id].children.push(node);
    } else {
      root = node;
    }
  });

  // IMPORTANT: Only unanswered suggestion placeholders ("?" nodes) are gated behind enableFollowups.
  // Actual child reflections (answered followups) are always included in the tree above.
  // When enableFollowups is OFF, the tree still shows answered/real children — only the
  // unanswered placeholder nodes are hidden. Ask the user before changing this behavior.
  if (enableFollowups) {
    flatReflections.forEach(r => {
      const node = byId[r.id];
      const unanswered = (r.ai_followup_suggestions || []).filter(s => !s.answered);
      unanswered.forEach((s, i) => {
        node.children.push({
          _placeholder: true,
          _placeholderId: `${r.id}-placeholder-${i}`,
          _parentId: r.id,
          question: s.question,
          children: [],
        });
      });
    });
  }

  return root;
}

/**
 * Compute d3 tree layout positions for all nodes
 */
function computeLayout(rootNode) {
  const d3Root = hierarchy(rootNode, d => d.children);
  const layout = d3Tree().nodeSize([CARD_W + H_GAP, CARD_H + V_GAP]);
  layout(d3Root);

  let minX = Infinity, maxX = -Infinity, maxY = 0;
  d3Root.each(node => {
    if (node.x < minX) minX = node.x;
    if (node.x > maxX) maxX = node.x;
    if (node.y > maxY) maxY = node.y;
  });

  const width = (maxX - minX) + CARD_W;
  const height = maxY + CARD_H + 10;

  return { d3Root, minX, width, height };
}

/**
 * Get all media type icons present in a reflection
 */
function getMediaIcons(reflection) {
  const icons = [];
  if (reflection.video_key) icons.push('\uD83C\uDFA5');
  if (reflection.audio_key) icons.push('\uD83C\uDFA4');
  if (reflection.has_images) icons.push('\uD83D\uDDBC\uFE0F');
  if (reflection.text_content?.trim()) icons.push('\uD83D\uDCDD');
  return icons;
}

/**
 * Tooltip content for a tree node
 */
function TreeTooltip({ data, timezone }) {
  if (data._placeholder) {
    return <div className="tree-tooltip-question">{data.question}</div>;
  }

  const question = data.followup_question || data.prompt_text;
  const mediaIcons = getMediaIcons(data);

  return (
    <>
      {question && <div className="tree-tooltip-question">{question}</div>}
      <div className="tree-tooltip-title">{data.title || 'Untitled'}</div>
      <div className="tree-tooltip-date">{formatCompactDate(data.created_at, timezone)}</div>
      {mediaIcons.length > 0 && (
        <div className="tree-tooltip-media">{mediaIcons.join(' ')}</div>
      )}
    </>
  );
}

/**
 * Check if a tree node can be enhanced (has text or media that could be transcribed)
 */
function canEnhanceNode(data) {
  if (data.ai_followup_status === 'generating') return false;
  const hasText = !!(data.text_content?.trim() ||
    data.video_transcription_text?.trim() ||
    data.audio_transcription_text?.trim());
  const hasMedia = !!(data.video_key || data.audio_key);
  return hasText || hasMedia;
}

/**
 * Check if a tree node needs transcription before enhancement
 */
function needsTranscription(data) {
  const hasText = !!(data.text_content?.trim() ||
    data.video_transcription_text?.trim() ||
    data.audio_transcription_text?.trim());
  return !hasText && !!(data.video_key || data.audio_key);
}

/**
 * Render the tree using d3-computed positions with SVG connectors
 */
function isTreeNodeNew(d, currentUserId) {
  if (!currentUserId || d._placeholder) return false;
  if (d.user_id === currentUserId) return false;
  if (d.badge_dismissed) return false;
  if (!d.studio_opened) return true;
  if (d.studio_opened_at) {
    return new Date(d.updated_at) > new Date(d.studio_opened_at);
  }
  return false;
}

function TreeView({ tree, currentReflectionId, onOpenReflection, onEnhance, onOpenFollowup, authorizeAI, enableFollowups, onTranscribeAndEnhance, currentUserId, searchText, timezone }) {
  const [hoverInfo, setHoverInfo] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const contextMenuRef = useRef(null);
  const isTreeOwner = tree.user_id === currentUserId;
  const { d3Root, minX, width, height } = computeLayout(tree);
  const nodes = d3Root.descendants();
  const links = d3Root.links();

  const handleMouseEnter = (e, nodeId, data) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHoverInfo({ id: nodeId, rect, data });
  };

  const handleMouseLeave = () => {
    setHoverInfo(null);
  };

  const handleContextMenu = (e, nodeData) => {
    e.preventDefault();
    e.stopPropagation();
    setHoverInfo(null);
    setContextMenu({
      nodeData,
      position: { top: e.clientY, left: e.clientX }
    });
  };

  // Close context menu on click-outside
  useEffect(() => {
    if (!contextMenu) return;
    const handleClickOutside = (event) => {
      if (contextMenuRef.current && contextMenuRef.current.contains(event.target)) return;
      setContextMenu(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contextMenu]);

  // Close context menu on scroll
  useEffect(() => {
    if (!contextMenu) return;
    const handleScroll = () => setContextMenu(null);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [contextMenu]);

  const handleEnhanceFromTree = (nodeData) => {
    if (needsTranscription(nodeData)) {
      if (onTranscribeAndEnhance) onTranscribeAndEnhance(nodeData.id);
    } else {
      if (onEnhance) onEnhance(nodeData.id);
    }
  };

  return (
    <div className="tree-layout" style={{ width, height }}>
      <svg className="tree-svg-layer" width={width} height={height}>
        {links.map((link, i) => {
          const sx = link.source.x - minX + CARD_W / 2;
          const sy = link.source.y + CARD_H;
          const tx = link.target.x - minX + CARD_W / 2;
          const ty = link.target.y;
          const midY = ty - 8;
          return (
            <path
              key={i}
              className="tree-connector"
              d={`M${sx},${sy} L${sx},${midY} L${tx},${midY} L${tx},${ty}`}
            />
          );
        })}
      </svg>

      {nodes.map(node => {
        const d = node.data;
        const nodeId = d._placeholder ? d._placeholderId : d.id;

        if (d._placeholder) {
          return (
            <div
              key={d._placeholderId}
              className="tree-mini-card placeholder"
              style={{
                position: 'absolute',
                left: node.x - minX,
                top: node.y,
              }}
              onMouseEnter={(e) => handleMouseEnter(e, nodeId, d)}
              onMouseLeave={handleMouseLeave}
              onContextMenu={isTreeOwner ? (e) => handleContextMenu(e, d) : undefined}
            >
              <div className="tree-mini-card-icon">?</div>
              <div className="tree-mini-card-title">
                {d.question}
              </div>
            </div>
          );
        }

        const isCurrent = d.id === currentReflectionId;
        const nodeIsNew = isTreeNodeNew(d, currentUserId);
        const nodeIsMatch = !!(searchText?.trim() && matchesSearch(d, searchText));
        return (
          <div
            key={d.id}
            className={`tree-mini-card ${isCurrent ? 'current' : ''}${nodeIsNew ? ' new' : ''}`}
            style={{
              position: 'absolute',
              left: node.x - minX,
              top: node.y,
            }}
            onClick={() => onOpenReflection && onOpenReflection(d)}
            onMouseEnter={(e) => handleMouseEnter(e, nodeId, d)}
            onMouseLeave={handleMouseLeave}
            onContextMenu={(e) => handleContextMenu(e, d)}
          >
            {nodeIsNew && <div className="tree-new-badge">NEW</div>}
            {nodeIsMatch && (
              <div className={`tree-match-badge ${nodeIsNew ? 'below-new' : ''}`}>Match</div>
            )}
            <div className="tree-mini-card-icon">{getMediaIcon(d)}</div>
            <div className="tree-mini-card-title">
              {d.title || 'Untitled'}
            </div>
            <div className="tree-mini-card-date">{formatCompactDate(d.created_at, timezone)}</div>
          </div>
        );
      })}

      {hoverInfo && ReactDOM.createPortal(
        <div
          className="tree-tooltip-portal"
          style={{
            position: 'fixed',
            left: hoverInfo.rect.left + hoverInfo.rect.width / 2,
            top: hoverInfo.rect.top - 6,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <TreeTooltip data={hoverInfo.data} timezone={timezone} />
        </div>,
        document.body
      )}

      {contextMenu && ReactDOM.createPortal(
        <div
          ref={contextMenuRef}
          className="reflection-context-menu"
          style={{
            position: 'fixed',
            top: contextMenu.position.top,
            left: contextMenu.position.left,
            zIndex: 99999
          }}
        >
          {contextMenu.nodeData._placeholder ? (
            isTreeOwner && (
              <button
                className="reflection-context-menu-item"
                onClick={() => {
                  if (onOpenFollowup) onOpenFollowup(contextMenu.nodeData._parentId, contextMenu.nodeData.question);
                  setContextMenu(null);
                }}
              >
                Respond
              </button>
            )
          ) : (
            <>
              <button
                className="reflection-context-menu-item"
                onClick={() => {
                  if (onOpenReflection) onOpenReflection(contextMenu.nodeData);
                  setContextMenu(null);
                }}
              >
                Open in Studio
              </button>
              {isTreeOwner && authorizeAI && enableFollowups && canEnhanceNode(contextMenu.nodeData) && (
                <button
                  className="reflection-context-menu-item"
                  onClick={() => {
                    handleEnhanceFromTree(contextMenu.nodeData);
                    setContextMenu(null);
                  }}
                >
                  {contextMenu.nodeData.ai_followup_suggestions?.length > 0 ? 'New Questions' : 'Enhance'}
                </button>
              )}
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

/**
 * ReflectionTree - Shows a visual tree of linked parent-child reflections
 * Appears on ReflectionCard for reflections that have children or a parent
 */
function ReflectionTree({ reflectionId, onOpenReflection, enableFollowups, followupStatus, enhancementCompleted, onEnhance, onOpenFollowup, authorizeAI, onTranscribeAndEnhance, lastEnhancementUpdate, initialExpanded = false, currentUserId, searchText, timezone }) {
  const [treeData, setTreeData] = useState(null);
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const [childCount, setChildCount] = useState(null);
  const [followupStats, setFollowupStats] = useState(null);

  // Auto-expand when initialExpanded prop changes to true after mount
  useEffect(() => {
    if (initialExpanded) {
      setIsExpanded(true);
    }
  }, [initialExpanded]);

  // Fetch tree data when reflectionId changes or followup status changes (e.g. after enhancement)
  useEffect(() => {
    let cancelled = false;
    const checkTree = async () => {
      try {
        const res = await api.get(`/reflections/${reflectionId}/tree`);
        if (!cancelled) {
          // Count unanswered suggestions across all nodes (only when followups enabled)
          const unansweredCount = enableFollowups ? res.data.reduce((sum, node) =>
            sum + ((node.ai_followup_suggestions || []).filter(s => !s.answered).length), 0) : 0;
          const totalChildren = res.data.length - 1 + unansweredCount;

          // Show toggle if there are child reflections or unanswered suggestions
          if (totalChildren > 0) {
            setChildCount(totalChildren);
            setTreeData(res.data);
          }

          // Compute aggregate followup stats across the entire tree
          let total = 0;
          let answered = 0;
          for (const node of res.data) {
            if (node.ai_followup_suggestions) {
              total += node.ai_followup_suggestions.length;
              answered += node.ai_followup_suggestions.filter(s => s.answered).length;
            }
          }
          if (total > 0) {
            setFollowupStats({ total, answered });
          }
        }
      } catch (err) {
        // Silently ignore - reflection may not have a tree
      }
    };
    checkTree();
    return () => { cancelled = true; };
  }, [reflectionId, followupStatus, lastEnhancementUpdate, enableFollowups]);

  // Auto-expand tree when enhancement completes (signaled by parent ReflectionCard)
  const prevEnhancementCompletedRef = useRef(0);
  useEffect(() => {
    if (enhancementCompleted > prevEnhancementCompletedRef.current) {
      setIsExpanded(true);
    }
    prevEnhancementCompletedRef.current = enhancementCompleted || 0;
  }, [enhancementCompleted]);

  if (!childCount) return null;

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const tree = treeData ? buildTree(treeData, enableFollowups) : null;

  // Build button label with follow-up stats
  let statsLabel = '';
  if (followupStats) {
    if (enableFollowups) {
      statsLabel = ` (${followupStats.answered}/${followupStats.total})`;
    } else if (followupStats.answered > 0) {
      statsLabel = ` (${followupStats.answered} answered)`;
    }
  }

  return (
    <>
      <div className="reflection-tree-toggle">
        <button onClick={handleToggle}>
          <span className="tree-toggle-arrow">{isExpanded ? '\u25B2' : '\u25BC'}</span>
          {' '}{isExpanded ? 'Hide' : 'View'} Followups{statsLabel}
        </button>
      </div>

      {isExpanded && tree && (
        <div className="reflection-tree-container">
          <TreeView
            tree={tree}
            currentReflectionId={reflectionId}
            onOpenReflection={onOpenReflection}
            onEnhance={onEnhance}
            onOpenFollowup={onOpenFollowup}
            authorizeAI={authorizeAI}
            enableFollowups={enableFollowups}
            onTranscribeAndEnhance={onTranscribeAndEnhance}
            currentUserId={currentUserId}
            searchText={searchText}
            timezone={timezone}
          />
        </div>
      )}
    </>
  );
}

export default ReflectionTree;
